import { deburr, isEmpty, isNil, upperFirst } from "lodash";
import { getCheckboxBase } from "../../anchors";
import { FIELD_GROUPS, zoneBaseName } from "../../data-fields";
import { editorText } from "../../editor-text";
import { searchable } from "utils/helpers/text";
import { ZONE_TYPES } from "../../field-status";

const LOOP_INDEX = "N";
const DIGITS = /^\d+$/;
const CONNECTORS = new Set(["of", "de", "du", "des", "d", "the", "la", "le"]);
const PERSON_QUALIFIERS = new Set([
  "CONTACT",
  "SPOUSE",
  "BROKER",
  "ADVISOR",
  "PAYER",
]);

const entries = (pairs) =>
  Object.fromEntries(
    pairs.flatMap(([keys, value]) => keys.map((key) => [key, value])),
  );

const PHRASES = entries([
  [
    ["lastname", "name", "nom", "surname", "familyname", "nomfamille"],
    "LASTNAME",
  ],
  [["firstname", "prenom", "prenoms", "givenname"], "FIRSTNAME"],
  [["birthname", "maidenname", "nomnaissance", "nomjeunefille"], "BIRTHNAME"],
  [
    ["maritalname", "usualname", "usedname", "nomusage", "nommarital"],
    "MARITALNAME",
  ],
  [
    ["birthdate", "dateofbirth", "dob", "datenaissance", "datedenaissance"],
    "BIRTHDATE",
  ],
  [
    [
      "birthplace",
      "birthcity",
      "placeofbirth",
      "cityofbirth",
      "lieunaissance",
      "lieudenaissance",
      "villenaissance",
    ],
    "BIRTHPLACE",
  ],
  [
    ["birthcountry", "countryofbirth", "paysnaissance", "paysdenaissance"],
    "BIRTHCOUNTRY",
  ],
  [
    ["birthdepartment", "departmentofbirth", "departementnaissance"],
    "BIRTHDEPARTMENT",
  ],
  [["nationality", "nationalite"], "NATIONALITY"],
  [["gender", "civility", "civilite", "title", "salutation"], "GENDER"],
  [["sex", "sexe"], "SEX"],
  [
    [
      "socialsecuritynumber",
      "ssn",
      "nir",
      "secu",
      "numerosecu",
      "numsecu",
      "numerosecuritesociale",
    ],
    "SOCIALSECURITYNUMBER",
  ],
  [
    ["socialregime", "regime", "regimesocial", "regimeobligatoire"],
    "SOCIALREGIME",
  ],
  [["fund", "healthfund", "caisse", "caissemaladie", "cpam"], "FUND"],
  [
    [
      "familysituation",
      "maritalstatus",
      "situationfamiliale",
      "situationdefamille",
    ],
    "FAMILYSITUATION",
  ],
  [["profession", "job", "occupation", "metier"], "PROFESSION"],
  [["socioprofessionalcategory", "csp"], "SOCIOPROFESSIONALCATEGORY"],
  [["relationship", "relation", "lien"], "RELATIONSHIP"],
  [["teletransmission", "noemie"], "TELETRANSMISSION"],
  [["politicallyexposedperson", "ppe"], "POLITICALLYEXPOSEDPERSON"],
  [["optmadelin", "madelin"], "OPTMADELIN"],
  [
    [
      "street",
      "address",
      "address1",
      "addressline",
      "addressline1",
      "adresse",
      "rue",
      "voie",
    ],
    "STREET",
  ],
  [["streetnumber", "numerorue", "numerovoie"], "STREETNUMBER"],
  [
    ["complement", "address2", "addressline2", "complementadresse"],
    "COMPLEMENT",
  ],
  [["zipcode", "postalcode", "postcode", "zip", "cp", "codepostal"], "ZIPCODE"],
  [["city", "town", "ville", "commune", "localite"], "CITY"],
  [["country", "pays"], "COUNTRY"],
  [["email", "mail", "emailaddress", "courriel", "adressemail"], "EMAIL"],
  [["phone", "phonenumber", "telephone", "tel"], "PHONE"],
  [["mobile", "mobilephone", "cellphone", "portable"], "MOBILE"],
  [["landline", "homephone", "fixe"], "LANDLINE"],
  [["formula", "formule", "formulaname", "formulalabel"], "FORMULA"],
  [["level", "niveau"], "LEVEL"],
  [
    [
      "product",
      "produit",
      "productname",
      "planname",
      "insuranceplanname",
      "offer",
      "offre",
    ],
    "PRODUCT",
  ],
  [
    ["contractnumber", "policynumber", "numerocontrat", "numcontrat"],
    "CONTRACTNUMBER",
  ],
  [
    ["adherentnumber", "membernumber", "numeroadherent", "numadherent"],
    "ADHERENTNUMBER",
  ],
  [["subscriptionnumber", "numerosouscription"], "SUBSCRIPTIONNUMBER"],
  [["quotenumber", "numerodevis"], "QUOTENUMBER"],
  [
    [
      "subscriptiondate",
      "souscriptiondate",
      "datesouscription",
      "datedesouscription",
    ],
    "SUBSCRIPTIONDATE",
  ],
  [
    ["effectivedate", "issuancedate", "dateeffet", "datedeffet"],
    "EFFECTIVEDATE",
  ],
  [["startdate", "datedebut"], "STARTDATE"],
  [["enddate", "datefin"], "ENDDATE"],
  [["signaturedate", "signeddate", "datesignature"], "SIGNATUREDATE"],
  [
    ["signatureplace", "placeofsignature", "lieusignature", "faita"],
    "SIGNATUREPLACE",
  ],
  [["place", "lieu"], "PLACE"],
  [["currentdate", "today", "datedujour", "datejour"], "CURRENTDATE"],
  [["quotevalidity"], "QUOTEVALIDITY"],
  [
    ["cost", "premium", "cotisation", "monthlycost", "cotisationmensuelle"],
    "COST",
  ],
  [["adherentcost"], "ADHERENTCOST"],
  [["spousecost"], "SPOUSECOST"],
  [["childrencost"], "CHILDRENCOST"],
  [["amount", "montant"], "AMOUNT"],
  [["price", "prix"], "PRICE"],
  [["tax", "taxes"], "TAX"],
  [["administrativefees", "fees", "frais"], "ADMINISTRATIVEFEES"],
  [["currency", "devise"], "CURRENCY"],
  [
    ["billingperiod", "frequency", "periodicity", "periodicite"],
    "BILLINGPERIOD",
  ],
  [["paymenttype", "paymentmode", "modepaiement"], "PAYMENTTYPE"],
  [["debitday", "jourprelevement"], "DEBITDAY"],
  [["owner", "holder", "titulaire"], "OWNER"],
  [["bank", "bankname", "banque"], "BANK"],
  [["iban"], "IBAN"],
  [["bic"], "BIC"],
  [["rib"], "RIB"],
  [["siret", "siretnumber"], "SIRET"],
  [["siren", "sirennumber"], "SIREN"],
  [["orias"], "ORIAS"],
  [["rcs", "rcsnumber"], "RCS"],
  [["rcsplace"], "RCSPLACE"],
  [
    ["socialreason", "raisonsociale", "companyname", "company", "societe"],
    "SOCIALREASON",
  ],
  [["brokercode", "codecourtier"], "BROKERCODE"],
  [["insurer", "assureur"], "INSURER"],
  [["adherent"], "ADHERENT"],
  [["conseiller"], "CONSEILLER"],
  [["date"], "DATE"],
  [["status", "statut"], "STATUS"],
  [["number", "numero", "num"], "NUMBER"],
  [["type"], "TYPE"],
  [["code"], "CODE"],
  [["identifier", "id"], "IDENTIFIER"],
  [["reference", "ref"], "REFERENCE"],
  [["label", "libelle"], "LABEL"],
  [["category", "categorie"], "CATEGORY"],
  [["age"], "AGE"],
  [["signature", "sign", "yousign", "yousing", "esign"], "SIGNATURE"],
]);

const HEADS = entries([
  [["date"], "DATE"],
  [["number", "numero", "num", "no"], "NUMBER"],
  [["name", "nom"], "NAME"],
  [["code"], "CODE"],
  [["place", "lieu"], "PLACE"],
  [["city", "ville"], "CITY"],
  [["country", "pays"], "COUNTRY"],
  [["day", "jour"], "DAY"],
  [["type"], "TYPE"],
  [["amount", "montant"], "AMOUNT"],
  [["mode"], "MODE"],
  [["rate", "taux"], "RATE"],
  [["label", "libelle"], "LABEL"],
  [["level", "niveau"], "LEVEL"],
  [["address", "adresse"], "ADDRESS"],
  [["phone", "telephone"], "PHONE"],
  [["reference", "ref"], "REFERENCE"],
  [["id", "identifiant"], "ID"],
]);

const WORDS = entries([
  [["birth", "naissance"], "BIRTH"],
  [["signature", "signed"], "SIGNATURE"],
  [["subscription", "souscription"], "SUBSCRIPTION"],
  [["effective", "effet"], "EFFECTIVE"],
  [["issuance"], "ISSUANCE"],
  [["start", "debut"], "START"],
  [["end", "fin"], "END"],
  [["contract", "policy", "contrat"], "CONTRACT"],
  [["quote", "devis"], "QUOTE"],
  [["adherent", "member"], "ADHERENT"],
  [["membership", "adhesion"], "MEMBERSHIP"],
  [["product", "plan", "produit"], "PRODUCT"],
  [["formula", "formule"], "FORMULA"],
  [["payment", "paiement"], "PAYMENT"],
  [["debit", "prelevement"], "DEBIT"],
  [["validity", "validite"], "VALIDITY"],
  [["termination", "resiliation"], "TERMINATION"],
  [["renewal"], "RENEWAL"],
  [["creation", "created"], "CREATION"],
  [["expiry", "expiration"], "EXPIRY"],
  [["due", "echeance"], "DUE"],
  [["account", "compte"], "ACCOUNT"],
  [["bank", "banque"], "BANK"],
  [["client", "customer"], "CLIENT"],
  [["project", "projet"], "PROJECT"],
  [["company"], "COMPANY"],
  [["family", "famille"], "FAMILY"],
  [["street", "rue"], "STREET"],
  [["file", "dossier"], "FILE"],
  [["request", "demande"], "REQUEST"],
  [["death", "deces"], "DEATH"],
  [["retirement", "retraite"], "RETIREMENT"],
  [["hire", "embauche"], "HIRE"],
]);

const PREFIXES = entries([
  [
    [
      "contact",
      "adherent",
      "member",
      "insured",
      "subscriber",
      "souscripteur",
      "assure",
      "principal",
    ],
    "CONTACT",
  ],
  [["spouse", "conjoint", "partner", "partenaire"], "SPOUSE"],
  [["children", "child", "enfant", "enfants", "kid", "kids"], "CHILDREN"],
  [
    [
      "rp",
      "relatedperson",
      "relatedpersons",
      "beneficiary",
      "beneficiaries",
      "beneficiaire",
      "beneficiaires",
      "ayantdroit",
    ],
    "RP",
  ],
  [["broker", "courtier"], "BROKER"],
  [["advisor", "conseiller", "agent"], "ADVISOR"],
  [["organization", "organisation", "cabinet"], "ORGANIZATION"],
  [["project", "projet"], "PROJECT"],
  [["insuranceplan", "plan", "product", "produit"], "INSURANCEPLAN"],
  [["subscription", "souscription"], "SUBSCRIPTION"],
  [["contract", "contrat"], "CONTRACT"],
  [["quote", "devis"], "QUOTE"],
  [["payment", "paiement"], "PAYMENT"],
  [["payer", "payeur", "debtor", "debiteur"], "PAYER"],
  [["receiver"], "RECEIVER"],
  [["bank", "bankaccount", "account", "compte"], "BANK"],
  [["mandate", "mandat", "sepa"], "MANDATE"],
  [
    ["renonciation", "renunciation", "waiver", "withdrawal", "retractation"],
    "RENONCIATION",
  ],
  [["termination", "resiliation"], "TERMINATION"],
  [["address", "adresse", "meta", "fields", "data", "identity", "user"], null],
]);

const CHECKBOX_BASES = entries([
  [["CIV", "CIVILITE"], "CIV"],
  [["SEXE", "SEX"], "SEXE"],
  [["TELE", "TELETRANS", "NOEMIE"], "TELE"],
  [["SIT", "SITFAM", "SF"], "SIT"],
  [["REG", "REGIME"], "REG"],
  [["PAI", "PAIEMENT", "MODE"], "PAI"],
  [["FREQ", "PERIO"], "FREQ"],
]);

const PREFIX_GROUPS = {
  contact: FIELD_GROUPS.MEMBER,
  adherent: FIELD_GROUPS.MEMBER,
  member: FIELD_GROUPS.MEMBER,
  spouse: FIELD_GROUPS.SPOUSE,
  conjoint: FIELD_GROUPS.SPOUSE,
  children: FIELD_GROUPS.CHILDREN,
  child: FIELD_GROUPS.CHILDREN,
  rp: FIELD_GROUPS.CHILDREN,
  relatedpersons: FIELD_GROUPS.CHILDREN,
  broker: FIELD_GROUPS.BROKER,
  conseiller: FIELD_GROUPS.BROKER,
  advisor: FIELD_GROUPS.BROKER,
  organization: FIELD_GROUPS.BROKER,
  project: FIELD_GROUPS.CONTRACT,
  insuranceplan: FIELD_GROUPS.CONTRACT,
  subscription: FIELD_GROUPS.CONTRACT,
  contract: FIELD_GROUPS.CONTRACT,
  payment: FIELD_GROUPS.PAYMENT,
};

export const normalizedKey = (text) =>
  deburr(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const wordsOf = (segment) =>
  segment
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/([A-Za-z])(\d)/g, "$1 $2")
    .replace(/(\d)([A-Za-z])/g, "$1 $2")
    .split(/[\s_-]+/)
    .map((word) => normalizedKey(word))
    .filter((word) => !isEmpty(word));

const headText = (word) => editorText(`ZONE_LABELS.HEADS.${HEADS[word]}.NOUN`);

const modifierText = (word) =>
  isNil(WORDS[word])
    ? editorText(`ZONE_LABELS.HEADS.${HEADS[word]}.OF`)
    : editorText(`ZONE_LABELS.WORDS.${WORDS[word]}`);

const isModifier = (word) => !isNil(WORDS[word]) || !isNil(HEADS[word]);

const composedText = (head, modifier) =>
  upperFirst(
    editorText("ZONE_LABELS.COMPOSED", {
      head: headText(head),
      modifier: modifierText(modifier),
    }),
  );

const composed = (words) => {
  if (words.length !== 2) return null;
  const [first, second] = words;
  if (!isNil(HEADS[second]) && isModifier(first))
    return composedText(second, first);
  if (!isNil(HEADS[first]) && isModifier(second))
    return composedText(first, second);
  return null;
};

const qualifierOf = (segment) => {
  const key = PREFIXES[normalizedKey(segment)];
  if (isNil(key)) return null;
  return {
    key,
    text: editorText(`ZONE_LABELS.QUALIFIERS.${key}.TEXT`),
    core: searchable(editorText(`ZONE_LABELS.QUALIFIERS.${key}.CORE`)),
  };
};

const fieldOf = (segment) => {
  const phrase = PHRASES[normalizedKey(segment)];
  if (!isNil(phrase))
    return {
      field: editorText(`ZONE_LABELS.PHRASES.${phrase}`),
      unknown: false,
    };
  const words = wordsOf(segment);
  const meaningful = words.filter((word) => !CONNECTORS.has(word));
  const field = composed(meaningful);
  return isNil(field)
    ? { field: null, unknown: true, words: words.join(" ") }
    : { field, unknown: false };
};

const segmentsOf = (zone) =>
  zoneBaseName(zone.id)
    .split(".")
    .filter(
      (segment) =>
        !isEmpty(segment) && segment !== LOOP_INDEX && !DIGITS.test(segment),
    );

const withoutRepeat = (parts, prefix) =>
  isNil(prefix?.text) ||
  (!isNil(parts.field) && searchable(parts.field).includes(prefix.core))
    ? { ...parts, qualifier: null, core: null }
    : { ...parts, qualifier: prefix.text, core: prefix.core };

const signatureParts = (zone) => {
  const person = segmentsOf(zone)
    .map(qualifierOf)
    .find((prefix) => PERSON_QUALIFIERS.has(prefix?.key));
  return {
    signature: true,
    field: null,
    unknown: false,
    qualifier: person?.text ?? null,
    core: person?.core ?? null,
  };
};

const checkboxParts = (zone) => {
  const base =
    getCheckboxBase(zone.anchors[0] ?? zone.id) ?? zone.id.slice(1, -1);
  const checkbox = CHECKBOX_BASES[base.toUpperCase()];
  const field = isNil(checkbox)
    ? null
    : editorText(`ZONE_LABELS.CHECKBOXES.${checkbox}`);
  return {
    checkbox: true,
    field,
    unknown: isNil(field),
    words: base,
    qualifier: null,
    core: null,
  };
};

export const zoneLabelParts = (zone) => {
  if (zone.type === ZONE_TYPES.SIGNATURE) return signatureParts(zone);
  if (zone.type === ZONE_TYPES.CHECKBOX_GROUP) return checkboxParts(zone);
  const segments = segmentsOf(zone);
  if (isEmpty(segments))
    return {
      field: null,
      unknown: true,
      words: zoneBaseName(zone.id),
      qualifier: null,
      core: null,
    };
  const parts = fieldOf(segments[segments.length - 1]);
  if (segments.length < 2) return { ...parts, qualifier: null, core: null };
  return withoutRepeat(parts, qualifierOf(segments[0]));
};

export const prefixGroup = (zone) => {
  const [first] = segmentsOf(zone);
  return isEmpty(first) ? undefined : PREFIX_GROUPS[first.toLowerCase()];
};
